import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { PATH_DASHBOARD } from '../routes/paths';
import useImpersonation from '../hooks/useImpersonation';
import {
  ImpersonationContext,
  clearImpersonationContext,
  readImpersonationContext,
} from '../utils/impersonationStorage';

// Phase 6.9: persistent banner shown on every dashboard page while the
// agent has an active impersonation context. Re-renders when the
// kayfa-impersonation-changed event fires and ticks every second so the
// countdown stays accurate.

function formatTimeLeft(ms: number): string {
  if (ms <= 0) return 'expired';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

export default function ImpersonationBanner() {
  const [ctx, setCtx] = useState<ImpersonationContext | null>(
    readImpersonationContext(),
  );
  const [now, setNow] = useState<number>(Date.now());
  const { endCurrent } = useImpersonation();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const sync = () => setCtx(readImpersonationContext());
    window.addEventListener('kayfa-impersonation-changed', sync);
    const tick = setInterval(() => {
      setNow(Date.now());
      // Drop the context the moment it expires.
      const current = readImpersonationContext();
      if (current === null && ctx !== null) {
        setCtx(null);
      }
    }, 1000);
    return () => {
      window.removeEventListener('kayfa-impersonation-changed', sync);
      clearInterval(tick);
    };
  }, [ctx]);

  if (!ctx) return null;

  const msLeft = ctx.expiresAt - now;

  const handleEnd = async () => {
    try {
      await endCurrent(ctx.accessToken);
    } catch {
      // We still clear locally even if the BE call fails — the agent
      // should not be stuck with a session they think is over.
    }
    clearImpersonationContext();
    setCtx(null);
    enqueueSnackbar('Impersonation ended', { variant: 'success' });
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar + 2,
      }}
    >
      <Alert
        severity="warning"
        icon={false}
        sx={{ borderRadius: 0, py: 0.5 }}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              component={Link}
              to={PATH_DASHBOARD.support.impersonate}
              size="small"
              color="inherit"
              variant="outlined"
            >
              Manage
            </Button>
            <Button size="small" color="inherit" variant="outlined" onClick={handleEnd}>
              End
            </Button>
          </Stack>
        }
      >
        <Typography variant="body2" component="span">
          <strong>Impersonating {ctx.target.email}</strong> · {ctx.reason} ·
          time left {formatTimeLeft(msLeft)}
        </Typography>
      </Alert>
    </Box>
  );
}
